import React, { useState, useEffect, useMemo, useContext } from "react";
import { intl, LanguageBaseContext, fetchData } from "dmsbasereact";
import { ReportLayout } from "./components/ReportLayout";
import { getIndicationShortText } from "../../constants/Indication";
import { useFormatters } from "../../hooks/useFormater";

/**
 * Get header columns for the prescriptions/dispensations report table
 * Sequência e textos EXATAMENTE conforme .ai_docs/reports.md
 * @param {Object} formatters - Formatters object with formatCNPJ function
 * @returns {Array} Array of column definitions
 */
const getHeaderColumns = (formatters) => [
    {
        label: intl.get("Reports.PrescDisp.PatientNumber").d("Patient Number"),
        column_name: "patient_number",
        width: "100px",
    },
    {
        label: intl.get("Reports.PrescDisp.RiskCategory").d("Risk Category"),
        column_name: "risk_category",
        width: "120px",
    },
    {
        label: intl.get("Reports.PrescDisp.PregnancyTestDate").d("Last Pregnancy Test Date"),
        column_name: "pregnancy_test_date",
        width: "130px",
    },
    {
        label: intl.get("Reports.PrescDisp.PregnancyTestResult").d("Test Result"),
        column_name: "pregnancy_test_result",
        width: "100px",
    },
    {
        label: intl.get("Reports.PrescDisp.MenstrualCycle").d("Menstrual Cycle"),
        column_name: "menstrual_cycle",
        width: "100px",
    },
    {
        label: intl.get("Reports.PrescDisp.InfertilityProof").d("Infertility Proven By"),
        column_name: "infertility_reason",
        width: "150px",
    },
    {
        label: intl.get("Reports.PrescDisp.ContraceptiveMethods").d("Contraceptive Methods Used"),
        column_name: "contraceptive_methods",
        width: "160px",
    },
    {
        label: intl.get("Reports.PrescDisp.EffectiveContraceptives").d("Effective Contraceptives"),
        column_name: "effective_contraceptives",
        width: "150px",
    },
    {
        label: intl.get("Reports.PrescDisp.BarrierContraceptive").d("Barrier Contraceptive"),
        column_name: "barrier_contraceptive",
        width: "150px",
    },
    {
        label: intl.get("Reports.PrescDisp.PrescriptionNumber").d("Prescription Number"),
        column_name: "prescription_identifier",
        width: "120px",
    },
    {
        label: intl.get("Reports.PrescDisp.Posology").d("Posology"),
        column_name: "posology_desc",
        width: "120px",
    },
    {
        label: intl.get("Reports.PrescDisp.TreatmentPause").d("Treatment Pause"),
        column_name: "dosage_pause",
        width: "100px",
    },
    {
        label: intl.get("Reports.PrescDisp.DosageNotes").d("Posology Additional Notes"),
        column_name: "dosage_notes",
        width: "160px",
    },
    {
        label: intl.get("Reports.PrescDisp.Presentation").d("Presentation"),
        column_name: "medication_name",
        width: "130px",
    },
    {
        label: intl.get("Reports.PrescDisp.CapsuleCount").d("Capsule Count"),
        column_name: "dosage_quantity",
        width: "100px",
    },
    {
        label: intl.get("Reports.PrescDisp.Indication").d("Treatment Indication"),
        column_name: "indication",
        width: "130px",
        function: (value) => getIndicationShortText(value)
    },
    {
        label: intl.get("Reports.PrescDisp.IndicationOtherDesc").d("Other Indication Description"),
        column_name: "indication_other_desc",
        width: "160px",
    },
    {
        label: intl.get("Reports.PrescDisp.AllowAlternativeDisp").d("Alternative Presentation Term Accepted"),
        column_name: "allow_alternative_disp",
        width: "180px",
    },
    {
        label: intl.get("Reports.PrescDisp.PrescriptionStatus").d("Prescription Status"),
        column_name: "prescription_status",
        width: "120px",
    },
    {
        label: intl.get("Reports.PrescDisp.PrescriptionDate").d("Prescription Date"),
        column_name: "prescription_date",
        width: "120px",
    },
    {
        label: intl.get("Reports.PrescDisp.ExpirationDate").d("Prescription Expiration Date"),
        column_name: "expiration_date",
        width: "140px",
    },
    {
        label: intl.get("Reports.PrescDisp.TermAcceptedDate").d("Liability Term Accepted Date"),
        column_name: "dosage_term_accepted",
        width: "180px",
    },
    {
        label: intl.get("Reports.PrescDisp.DoctorName").d("Doctor Name"),
        column_name: "doctor_name",
        width: "150px",
    },
    {
        label: intl.get("Reports.PrescDisp.DoctorCRM").d("CRM"),
        column_name: "doctor_crm",
        width: "80px",
    },
    {
        label: intl.get("Reports.PrescDisp.DoctorState").d("Prescriber State"),
        column_name: "doctor_state",
        width: "80px",
    },
    {
        label: intl.get("Reports.PrescDisp.PrescriberCNPJ").d("Prescriber Institution CNPJ"),
        column_name: "presc_inst_cnpj",
        width: "150px",
        function: (value) => formatters.formatCNPJ(value),
    },
    {
        label: intl.get("Reports.PrescDisp.Dispensed").d("Dispensation Done?"),
        column_name: "dispensed",
        width: "120px",
    },
    {
        label: intl.get("Reports.PrescDisp.DispensationDate").d("Dispensation Date"),
        column_name: "disp_date",
        width: "120px",
    },
    {
        label: intl.get("Reports.PrescDisp.DeliveryDate").d("Medication Delivery Date"),
        column_name: "delivery_date",
        width: "140px",
    },
    {
        label: intl.get("Reports.PrescDisp.PharmacistName").d("Dispensing Pharmacist"),
        column_name: "pharmacist_name",
        width: "150px",
    },
    {
        label: intl.get("Reports.PrescDisp.PharmacistCRF").d("CRF"),
        column_name: "pharmacist_crf",
        width: "80px",
    },
    {
        label: intl.get("Reports.PrescDisp.PharmacistState").d("Dispenser State"),
        column_name: "pharmacist_state",
        width: "80px",
    },
    {
        label: intl.get("Reports.PrescDisp.DispenserInst").d("Dispensing Institution"),
        column_name: "disp_inst_name",
        width: "150px",
    },
    {
        label: intl.get("Reports.PrescDisp.DispenserCNPJ").d("Dispensing Institution CNPJ"),
        column_name: "disp_inst_cnpj",
        width: "150px",
        function: (value) => formatters.formatCNPJ(value),
    },
    {
        label: intl.get("Reports.PrescDisp.DispenserAddress").d("Dispensing Institution Address"),
        column_name: "disp_inst_address",
        width: "160px",
    },
    {
        label: intl.get("Reports.PrescDisp.DispenserCity").d("Dispensing Institution City"),
        column_name: "disp_inst_city",
        width: "140px",
    },
    {
        label: intl.get("Reports.PrescDisp.DispenserState").d("Dispensing Institution State"),
        column_name: "disp_inst_state",
        width: "100px",
    },
];

/**
 * Get filter fields for the report
 * @returns {Array} Array of filter field definitions
 */
const getFilterFields = () => [
    { name: "patient_number", label: intl.get("Reports.PrescDisp.FilterPatientNumber").d("Patient Number"), type: "text" },
    { name: "prescription_identifier", label: intl.get("PrescriptionNumber").d("Prescription Number"), type: "text" },
    { name: "doctor_name", label: intl.get("Doctor").d("Doctor"), type: "text" },
    { name: "doctor_crm", label: intl.get("CRM").d("CRM"), type: "text" },
    { name: "pharmacist_name", label: intl.get("Pharmacist").d("Pharmacist"), type: "text" },
    { name: "pharmacist_crf", label: intl.get("CRF").d("CRF"), type: "text" },
    { name: "institution_name", label: intl.get("Reports.PrescDisp.FilterInstitution").d("Institution"), type: "text" },
    { name: "presc_inst_cnpj", label: intl.get("Reports.PrescDisp.FilterPrescriberCNPJ").d("Prescriber CNPJ"), type: "text" },
    { name: "disp_inst_cnpj", label: intl.get("Reports.PrescDisp.FilterDispenserCNPJ").d("Dispenser CNPJ"), type: "text" },
    { name: "batch_name", label: intl.get("Batch").d("Batch"), type: "text" },
    { name: "prescription_status", label: intl.get("Reports.PrescDisp.FilterPrescriptionStatus").d("Prescription Status"), type: "multiselect", optionsKey: "prescription_status" },
];

export const PrescriptionsDispensationsReportPage = () => {
    const { language } = useContext(LanguageBaseContext);
    const formatters = useFormatters();
    const [maxBatches, setMaxBatches] = useState(1);

    useEffect(() => {
        fetchData("/reports/prescriptions-dispensations/filter-options", "GET", true)
            .then((response) => {
                const data = response?.data?.data || response?.data || {};
                setMaxBatches(data.max_batches || 1);
            })
            .catch(() => setMaxBatches(1));
    }, []);

    const header = useMemo(() => {
        const baseColumns = getHeaderColumns(formatters);
        const batchColumns = [];
        for (let i = 1; i <= maxBatches; i++) {
            const suffix = maxBatches > 1 ? ` ${i}` : "";
            batchColumns.push(
                {
                    label: intl.get("Reports.PrescDisp.BatchNumber").d("Dispensed Batch") + suffix,
                    column_name: `batch_${i}_number`,
                    width: "110px",
                },
                {
                    label: intl.get("Reports.PrescDisp.BatchPresentation").d("Dispensed Presentation") + suffix,
                    column_name: `batch_${i}_presentation`,
                    width: "130px",
                },
                {
                    label: intl.get("Reports.PrescDisp.BatchBoxCount").d("Qty. (box)") + suffix,
                    column_name: `batch_${i}_box_count`,
                    width: "80px",
                },
                {
                    label: intl.get("Reports.PrescDisp.BatchCapsules").d("Dispensed Capsule Count") + suffix,
                    column_name: `batch_${i}_capsules`,
                    width: "140px",
                },
                {
                    label: intl.get("Reports.PrescDisp.BatchFabrication").d("Medication Fabrication Date") + suffix,
                    column_name: `batch_${i}_fabrication`,
                    width: "160px",
                },
                {
                    label: intl.get("Reports.PrescDisp.BatchExpiration").d("Medication Expiration Date") + suffix,
                    column_name: `batch_${i}_expiration`,
                    width: "150px",
                },
            );
        }
        return [...baseColumns, ...batchColumns];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is required for i18n updates
    }, [formatters, language, maxBatches]);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is required for i18n updates
    const filterFields = useMemo(() => getFilterFields(), [language]);

    return (
        <ReportLayout
            key={`prescriptions-dispensations-report-${language}`}
            title="Reports.PrescDisp.PageTitle"
            titleDefault="Relatório de Prescrições e Dispensações"
            manualSlug="reports-prescriptions-dispensations"
            tableUrl="/reports/prescriptions-dispensations/table"
            filterOptionsUrl="/reports/prescriptions-dispensations/filter-options"
            exportUrl="/reports/prescriptions-dispensations/export"
            header={header}
            filterFields={filterFields}
            dateFilterLabel={intl.get("Reports.DateFilter.PrescriptionDate").d("Prescription Date")}
        />
    );
};

export default PrescriptionsDispensationsReportPage;
